import React from "react";

import { Column } from "primereact/column";
import { PaginatorPageChangeEvent } from "primereact/paginator";

import {
  RoiSelectorColumn,
  RoiSelectorRow,
  RoiSelectorSection,
} from "../model/roi-request-create.types";
import {
  TablePaginationHeaderControls,
  TablePaginatorFooter,
} from "../../../shared/ui/data-table/ui/table-paginator-footer";
import { renderTableSortIcon } from "../../../shared/ui/data-table/ui/table-sort-icon";
import * as Styled from "./roi-request-create-selector.styled";
import { ReactComponent as SearchEmptyIcon } from "./assets/search-empty.svg";

interface IRoiRequestCreateSelectorTableProps {
  section: RoiSelectorSection;
  searchTerm: string;
  // eslint-disable-next-line no-unused-vars
  onSearchTermChange?: (...args: [string]) => void;
  selectedIds: string[];
  // eslint-disable-next-line no-unused-vars
  onToggleRowSelection?: (...args: [string, string]) => void;
  // eslint-disable-next-line no-unused-vars
  onToggleAllRowsInSection?: (...args: [string, string[]]) => void;
  isReadonly?: boolean;
}

const compareValues = (
  leftValue: RoiSelectorRow[keyof RoiSelectorRow],
  rightValue: RoiSelectorRow[keyof RoiSelectorRow],
) => {
  if (leftValue == null && rightValue == null) return 0;
  if (leftValue == null) return -1;
  if (rightValue == null) return 1;

  if (typeof leftValue === "number" && typeof rightValue === "number") {
    return leftValue - rightValue;
  }

  return String(leftValue).localeCompare(String(rightValue), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

export const RoiRequestCreateSelectorTable = ({
  section,
  searchTerm,
  onSearchTermChange,
  selectedIds,
  onToggleRowSelection,
  onToggleAllRowsInSection,
  isReadonly = false,
}: IRoiRequestCreateSelectorTableProps) => {
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [first, setFirst] = React.useState(0);
  const [sortField, setSortField] = React.useState<string | null>(null);
  const [sortOrder, setSortOrder] = React.useState<1 | -1 | 0>(0);
  const [searchInput, setSearchInput] = React.useState(searchTerm);

  React.useEffect(() => {
    setRowsPerPage(5);
    setFirst(0);
    setSortField(null);
    setSortOrder(0);
  }, [section.section_id]);

  React.useEffect(() => {
    setFirst(0);
  }, [searchTerm, section.table.rows]);

  React.useEffect(() => {
    setSearchInput(searchTerm);
  }, [searchTerm, section.section_id]);

  const filteredRows = React.useMemo(() => {
    if (searchTerm.trim().length === 0) {
      return section.table.rows;
    }

    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    return section.table.rows.filter((row) =>
      section.table.columns.some((column) => {
        const value = row[column.key];

        return String(value ?? "")
          .toLowerCase()
          .includes(normalizedSearchTerm);
      }),
    );
  }, [searchTerm, section]);

  const sortedRows = React.useMemo(() => {
    if (!sortField || sortOrder === 0) {
      return filteredRows;
    }

    return [...filteredRows].sort((leftRow, rightRow) => {
      const comparison = compareValues(leftRow[sortField], rightRow[sortField]);

      return sortOrder === -1 ? comparison * -1 : comparison;
    });
  }, [filteredRows, sortField, sortOrder]);

  React.useEffect(() => {
    if (first >= sortedRows.length && first !== 0) {
      setFirst(0);
    }
  }, [first, sortedRows.length]);

  const paginatedRows = React.useMemo(
    () => sortedRows.slice(first, first + rowsPerPage),
    [first, rowsPerPage, sortedRows],
  );
  const selectedIdSet = React.useMemo(
    () => new Set(selectedIds),
    [selectedIds],
  );
  const selectionRenderKey = React.useMemo(
    () => selectedIds.join(","),
    [selectedIds],
  );

  const visibleRowIds = React.useMemo(
    () => filteredRows.map((row) => row.data_element_id),
    [filteredRows],
  );
  const hasVisibleRows = visibleRowIds.length > 0;
  const isAllVisibleSelected =
    hasVisibleRows && visibleRowIds.every((rowId) => selectedIdSet.has(rowId));

  const rowsPerPageOptions = [5, 10, 25, 50];

  const checkboxHeaderTemplate = () => (
    <Styled.CheckboxCell>
      <Styled.CheckboxInput
        type="checkbox"
        checked={isAllVisibleSelected}
        onClick={(event) => event.stopPropagation()}
        onChange={() =>
          onToggleAllRowsInSection?.(section.section_id, visibleRowIds)
        }
        disabled={!hasVisibleRows}
        aria-label={`Select all rows in ${section.label}`}
      />
    </Styled.CheckboxCell>
  );

  const checkboxBodyTemplate = (row: RoiSelectorRow) => (
    <Styled.CheckboxCell>
      <Styled.CheckboxInput
        key={`${row.data_element_id}-${selectedIdSet.has(row.data_element_id)}`}
        type="checkbox"
        checked={selectedIdSet.has(row.data_element_id)}
        onClick={(event) => event.stopPropagation()}
        onChange={() =>
          onToggleRowSelection?.(section.section_id, row.data_element_id)
        }
        aria-label={`Select row ${row.data_element_id}`}
      />
    </Styled.CheckboxCell>
  );

  const renderColumn = (column: RoiSelectorColumn) => (
    <Column
      key={column.key}
      field={column.key}
      header={column.header}
      sortable={!!column.sortable}
      body={(row: RoiSelectorRow) => row[column.key] ?? "—"}
    />
  );

  if (section.table.rows.length === 0) {
    return (
      <Styled.TableWrapper>
        <Styled.SearchEmptyState>
          <Styled.SearchEmptyIconWrapper>
            <SearchEmptyIcon aria-hidden="true" />
          </Styled.SearchEmptyIconWrapper>
          <Styled.SearchEmptyTextBlock>
            <Styled.SearchEmptyText>No records found.</Styled.SearchEmptyText>
            <Styled.SearchEmptyText>
              This selector section is configured, but it does not contain any
              records yet.
            </Styled.SearchEmptyText>
          </Styled.SearchEmptyTextBlock>
        </Styled.SearchEmptyState>
      </Styled.TableWrapper>
    );
  }

  return (
    <React.Fragment>
      <Styled.RowsPerPagePanelStyles />
      <Styled.TableWrapper>
        <Styled.Toolbar>
          <TablePaginationHeaderControls
            first={first}
            rows={rowsPerPage}
            totalRecords={sortedRows.length}
            rowsPerPageOptions={rowsPerPageOptions}
            onRowsChange={(rows) => {
              setRowsPerPage(rows);
              setFirst(0);
            }}
          />
          {!isReadonly ? (
            <Styled.SearchInputWrapper
              onSubmit={(event) => {
                event.preventDefault();
                onSearchTermChange?.(searchInput);
              }}
            >
              <Styled.SearchInput
                type="text"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search..."
              />
              <Styled.SearchInputIcon
                type="submit"
                className="pi pi-search"
                aria-label="Search"
              />
            </Styled.SearchInputWrapper>
          ) : null}
        </Styled.Toolbar>
        {sortedRows.length > 0 ? (
          <Styled.Table
            key={`${section.section_id}-${selectionRenderKey}`}
            className={isReadonly ? "is-readonly" : undefined}
            dataKey="data_element_id"
            value={paginatedRows}
            rowHover
            removableSort
            sortIcon={renderTableSortIcon}
            tableStyle={{ minWidth: "20rem" }}
            emptyMessage="No records found."
            sortField={sortField ?? undefined}
            sortOrder={sortOrder}
            onRowClick={
              isReadonly || !onToggleRowSelection
                ? undefined
                : (event) =>
                    onToggleRowSelection(
                      section.section_id,
                      event.data.data_element_id,
                    )
            }
            onSort={(sortEvent) => {
              const nextSortOrder =
                (sortEvent.sortOrder as 1 | -1 | 0 | null | undefined) ?? 0;

              setSortField(
                nextSortOrder === 0
                  ? null
                  : ((sortEvent.sortField as string | undefined) ?? null),
              );
              setSortOrder(nextSortOrder);
            }}
          >
            {!isReadonly ? (
              <Column
                header={checkboxHeaderTemplate()}
                body={checkboxBodyTemplate}
                align="center"
                alignHeader="center"
                style={{ width: "52px" }}
              />
            ) : null}
            {section.table.columns.map(renderColumn)}
          </Styled.Table>
        ) : (
          <Styled.SearchEmptyState>
            <Styled.SearchEmptyIconWrapper>
              <SearchEmptyIcon aria-hidden="true" />
            </Styled.SearchEmptyIconWrapper>
            <Styled.SearchEmptyTextBlock>
              <Styled.SearchEmptyText>No records found.</Styled.SearchEmptyText>
              <Styled.SearchEmptyText>
                Check the date range filter if it&apos;s enabled.
              </Styled.SearchEmptyText>
            </Styled.SearchEmptyTextBlock>
          </Styled.SearchEmptyState>
        )}
        <TablePaginatorFooter
          first={first}
          rows={rowsPerPage}
          totalRecords={sortedRows.length}
          rowsPerPageOptions={rowsPerPageOptions}
          onPageChange={(event: PaginatorPageChangeEvent) => {
            setFirst(event.first);
            setRowsPerPage(event.rows);
          }}
        />
      </Styled.TableWrapper>
    </React.Fragment>
  );
};
